import React, { useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

/**
 * Per-instance id source. React 16 has no `useId`, and react-select renders
 * its own input, which has to carry the id the label points at.
 */
let nextSelectId = 0;

/**
 * Shared react-select appearance for the meters-list filters.
 */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    maxHeight: 76,
    overflowY: 'auto',
    borderRadius: 6,
    fontSize: '0.875rem',
    borderColor: state.isFocused ? '#3f88a5' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(63, 136, 165, 0.25)' : 'none',
    '&:hover': {
      borderColor: '#3f88a5'
    }
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
    gap: 3
  }),
  multiValue: (base) => ({
    ...base,
    margin: 2,
    maxWidth: 190,
    backgroundColor: 'rgba(63, 136, 165, 0.05)',
    borderRadius: '4px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#3f88a5',
    fontWeight: 600,
    fontSize: '0.74rem',
    padding: '2px 3px 2px 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#3f88a5',
    ':hover': {
      backgroundColor: '#3f88a5',
      color: 'white'
    }
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '0.875rem',
    color: '#6b7280'
  })
};

/**
 * Labelled multi-select filter.
 *
 * Owns no state: the selected values are resolved from `values` against `options`
 * on every render, and changes are reported as a plain array of option values
 * (or undefined once nothing is selected, so the caller can drop the filter).
 *
 * @param {string} label - Field label shown above the control.
 * @param {Array<{value: *, label: string}>} options - Selectable options.
 * @param {Array<*>} values - Currently selected option values.
 * @param {Function} onChange - Called with the selected values, or undefined when empty.
 * @param {string} placeholder - Placeholder shown when nothing is selected.
 */
const MultiSelectFilter = ({ label, options, values, onChange, placeholder }) => {
  const inputId = useRef(`im-multiselect-${(nextSelectId += 1)}`).current;

  const selected = useMemo(() => {
    const optionsByValue = new Map(options.map((option) => [option.value, option]));

    return (values || []).map(
      (value) => optionsByValue.get(value) || { value, label: String(value) }
    );
  }, [values, options]);

  const handleChange = (selectedOptions) => {
    const nextValues = (selectedOptions || []).map((option) => option.value);
    onChange(nextValues.length ? nextValues : undefined);
  };

  /**
   * Chips show the short label so a selection stays one line; the dropdown keeps
   * the full label, so a meter is still findable by its ID.
   */
  const renderOption = (option, { context }) =>
    context === 'value' ? option.shortLabel || option.label : option.label;

  return (
    <Form.Group>
      <Form.Label htmlFor={inputId}>{label}</Form.Label>
      <Select
        inputId={inputId}
        isMulti
        options={options}
        value={selected}
        onChange={handleChange}
        placeholder={placeholder}
        formatOptionLabel={renderOption}
        styles={selectStyles}
        className="react-select-container im-select"
        classNamePrefix="react-select"
        isClearable
        closeMenuOnSelect={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </Form.Group>
  );
};

export default MultiSelectFilter;
